import {
  provideBrowserGlobalErrorListeners, isDevMode, Injectable,
  Provider, EnvironmentProviders, provideAppInitializer, inject,
} from '@angular/core';

import { Router } from '@angular/router';

import {
  provideTransloco, provideTranslocoMissingHandler, TranslocoConfig,
  TranslocoMissingHandler,
} from '@jsverse/transloco';

import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { TranslocoHttpLoader } from './core/utils/transloco-loader';
import { environment } from './app.environment';
import { provideFirebase } from './core/firebase';
import { provideAuthentication } from './auth';
import { provideDataLayer } from './data-layer';
import { AppStore, provideAppStore } from './app.store';
import { LanguageService, provideServices, ThemeService } from './services';
import { logInfo } from 'in-time-logger';
import { initializeBrowserDependencies, initializeServerDependencies } from './app.initializer';

@Injectable()
export class DefaultMissingHandler implements TranslocoMissingHandler {
  handle(key: string, config: TranslocoConfig) {
    if(config.missingHandler.logMissingKey && !config.prodMode) {
      const msg = `Missing translation for '${key}'`;
      // eslint-disable-next-line no-console
      console.warn(`%c ${msg}`, 'font-size: 12px; color: red');
    }

    return config.prodMode ? null : key;
  }
}

export function getCommonProviders(platform: 'browser' | 'server'): Array<Provider | EnvironmentProviders> {
  const providers: Array<Provider | EnvironmentProviders> = [];

  providers.push(provideBrowserGlobalErrorListeners());

  providers.push(provideClientHydration(withEventReplay()));
  providers.push(provideHttpClient(withFetch()));

  providers.push(provideTransloco({
    config: {
      availableLangs: [ 'en', 'hu', 'ro' ],
      defaultLang: environment.defaultLanguage,
      reRenderOnLangChange: true,
      prodMode: !isDevMode(),
    },
    loader: TranslocoHttpLoader
  }));

  if(platform === 'browser') {
    providers.push(provideTranslocoMissingHandler(DefaultMissingHandler));
  }

  providers.push(provideFirebase());
  providers.push(provideAuthentication());
  providers.push(provideDataLayer());
  providers.push(provideAppStore());
  providers.push(provideServices());

  providers.push(provideAppInitializer(async () => {
    const languageService = inject(LanguageService);
    const themeService = inject(ThemeService);
    const appStore = inject(AppStore);
    const router = inject(Router);

    logInfo('App is initializing...');

    if(platform === 'server') {
      initializeServerDependencies();
    }
    else {
      initializeBrowserDependencies();
    }

    themeService.load();
    await languageService.load();

    await appStore.checkLogin();

    logInfo('App has been initialized.');

    router.initialNavigation();
  }));

  return providers;
}
